/* eslint-disable react-hooks/set-state-in-effect -- this page uses the standard initial-load pattern (useCallback + useEffect) which sets state. The rule is too strict for this established pattern used across the admin pages. */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3, Users, Crown, Eye, TrendingUp, Loader2, Save,
  Globe, BookOpen, Newspaper, MessageSquare, Radio, Settings,
  ArrowUpRight, Activity, Heart, Share2, Clock, CheckCircle2, Repeat, UserX, Rocket, Megaphone, Lightbulb
} from 'lucide-react';
import { CampaignsPanel } from '@/components/admin/CampaignsPanel';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';

const TABS = [
  { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
  { id: 'visitors', label: 'Visitantes', icon: Globe },
  { id: 'users', label: 'Utilizadores', icon: Users },
  { id: 'premium', label: 'Premium', icon: Crown },
  { id: 'content', label: 'Conteúdo', icon: BookOpen },
  { id: 'engagement', label: 'Engagement', icon: Activity },
  { id: 'retention', label: 'Retenção', icon: Repeat },
  { id: 'growth', label: 'Crescimento', icon: Rocket },
  { id: 'campaigns', label: 'Campanhas', icon: Megaphone },
  { id: 'funnel', label: 'Funil Premium', icon: TrendingUp },
  { id: 'sources', label: 'Tráfego', icon: ArrowUpRight },
  { id: 'integrations', label: 'Integrações', icon: Settings },
];

const PALETTE = ['#9b111e', '#c5364a', '#e88b9b', '#d4af37', '#3b82f6', '#10b981', '#a855f7', '#f59e0b'];

function StatCard({ title, value, hint, icon: Icon, color = 'ruby', testid }) {
  return (
    <Card className="bg-surface border-border" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
            {hint && <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-4">{hint}</p>}
          </div>
          <div className={`p-2.5 rounded-lg bg-${color}/15 shrink-0`}>
            <Icon className={`w-5 h-5 text-${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, description, children, testid }) {
  return (
    <Card className="bg-surface border-border" data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-foreground text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function TopList({ title, items, valueLabel, valueKey, titleKey = 'title', emptyText = 'Sem dados', testid }) {
  return (
    <Card className="bg-surface border-border" data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-foreground text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {(!items || items.length === 0) && (
          <p className="text-sm text-muted-foreground py-4 text-center">{emptyText}</p>
        )}
        {items?.map((it, i) => (
          <div key={it.id || i} className="flex items-center gap-3 p-2 rounded-md bg-white/5 hover:bg-white/10 transition-colors">
            <span className="w-6 h-6 rounded-full bg-ruby/20 text-ruby text-xs flex items-center justify-center font-bold shrink-0">
              {i + 1}
            </span>
            {it.cover_image ? (
              <img src={it.cover_image} alt="" className="w-9 h-12 object-cover rounded shrink-0" />
            ) : (
              <div className="w-9 h-12 rounded bg-white/5 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground truncate">{it[titleKey] || it.name || it.preview || '—'}</p>
              <p className="text-xs text-muted-foreground">{valueLabel}: {it[valueKey] ?? 0}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function AdminAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const consentFrom = visitors?.consent_counting_since ? new Date(visitors.consent_counting_since).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Lisbon' }) : null;
  const [users, setUsers] = useState(null);
  const [premium, setPremium] = useState(null);
  const [content, setContent] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [settings, setSettings] = useState({ ga4_measurement_id: '', clarity_project_id: '' });
  const [savingSettings, setSavingSettings] = useState(false);
  const [visitorsPeriod, setVisitorsPeriod] = useState('month');
  const [retention, setRetention] = useState(null);
  const [retentionDays, setRetentionDays] = useState(30);

  useEffect(() => {
    if (tab !== 'retention') return;
    api.get(`/admin/analytics/retention?days=${retentionDays}`)
      .then(r => setRetention(r.data))
      .catch(() => toast.error('Erro ao carregar retenção'));
  }, [tab, retentionDays]);
  const retSince = retention?.tracking_since ? new Date(`${retention.tracking_since}T12:00:00`).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' }) : null;
  const rateValue = (r) => (r ? `${r.rate}%` : 'Dados insuficientes');
  const rateHint = (r, n) => (r ? `${r.returned} de ${r.cohort} contas voltaram no ${n}.º dia após o registo` : `Precisa de registos com o ${n}.º dia já concluído desde o início da recolha`);
  const [growth, setGrowth] = useState(null);
  const [growthDays, setGrowthDays] = useState(30);

  useEffect(() => {
    if (tab !== 'growth') return;
    api.get(`/admin/analytics/growth?days=${growthDays}`)
      .then(r => setGrowth(r.data))
      .catch(() => toast.error('Erro ao carregar crescimento'));
  }, [tab, growthDays]);
  const growthSeries = growth ? growth.registrations.map((r, i) => ({ date: r.date, registos: r.n, premium: growth.premium_sales[i]?.n ?? 0 })) : [];

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, vi, us, pr, co, en, fu, so, st] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get(`/admin/analytics/visitors?period=${visitorsPeriod}`),
        api.get('/admin/analytics/users'),
        api.get('/admin/analytics/premium'),
        api.get('/admin/analytics/content'),
        api.get('/admin/analytics/engagement'),
        api.get('/admin/analytics/funnel'),
        api.get('/admin/analytics/sources'),
        api.get('/admin/analytics/settings'),
      ]);
      setOverview(ov.data);
      setVisitors(vi.data);
      setUsers(us.data);
      setPremium(pr.data);
      setContent(co.data);
      setEngagement(en.data);
      setFunnel(fu.data);
      setSources(so.data);
      setSettings(st.data);
    } catch (e) {
      toast.error('Erro ao carregar analytics');
    } finally {
      setLoading(false);
    }
  }, [visitorsPeriod]);

  // Initial load — same pattern as AdminPoints, AdminSubscribers, etc.
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/home');
      return;
    }
    loadAll();
  }, [user, navigate, loadAll]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/analytics/settings', settings);
      toast.success('Definições guardadas. Recarrega para aplicar.');
    } catch {
      toast.error('Erro ao guardar definições');
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  const chartAxis = { stroke: 'rgba(255,255,255,0.4)', fontSize: 11 };
  const chartTooltip = {
    contentStyle: { background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 },
    labelStyle: { color: '#fff' },
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-7 h-7 text-ruby" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Analytics &amp; Insights</h1>
            <p className="text-muted-foreground text-sm">Visitantes, utilizadores, Premium, conteúdo e funil — tudo em tempo real.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="analytics-tabs">
          {TABS.map(t => (
            <Button
              key={t.id}
              variant={tab === t.id ? 'default' : 'outline'}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`shrink-0 ${tab === t.id ? 'bg-ruby hover:bg-ruby-light' : 'border-border'}`}
            >
              <t.icon className="w-4 h-4 mr-2" />
              {t.label}
            </Button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && overview && (
          <div className="space-y-6" data-testid="overview-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard testid="card-visitors-today" title="Visitantes Hoje" value={overview.visitors_today} icon={Globe} color="blue-400" hint="Visitantes únicos que aceitaram estatísticas, sem robôs" />
              <StatCard testid="card-new-users" title="Novos Registos Hoje" value={overview.new_users_today} icon={Users} color="emerald-400" hint="Contas criadas desde as 00h (Lisboa)" />
              <StatCard testid="card-premium-active" title="Premium Ativos" value={overview.premium_active} icon={Crown} color="gold" hint="Premium com data de fim futura" />
              <StatCard testid="card-premium-sales" title="Vendas Premium (mês)" value={overview.premium_sales_this_month} icon={TrendingUp} color="ruby" hint={`Compras pagas este mês · ${overview.premium_grants_this_month ?? 0} ativação(ões) manual(ais) à parte`} />
              <StatCard testid="card-top-story" title="Conto Mais Visto" value={overview.top_story?.views ?? 0} hint={overview.top_story?.title || '—'} icon={BookOpen} color="purple-400" />
              <StatCard testid="card-top-series" title="Série Mais Vista" value={overview.top_series?.views ?? 0} hint={overview.top_series?.title || '—'} icon={Newspaper} color="amber-400" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Tráfego (últimos 30 dias)" description="Page views (todas) e visitantes únicos com consentimento; antes do consentimento só há page views">
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={visitors?.series || []}>
                    <defs>
                      <linearGradient id="gradV" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9b111e" stopOpacity={0.7} />
                        <stop offset="95%" stopColor="#9b111e" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradP" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                    <YAxis {...chartAxis} allowDecimals={false} />
                    <Tooltip {...chartTooltip} />
                    <Area type="monotone" dataKey="page_views" stroke="#3b82f6" fillOpacity={1} fill="url(#gradP)" name="Page views" />
                    <Area type="monotone" dataKey="visitors" stroke="#9b111e" fillOpacity={1} fill="url(#gradV)" name="Visitantes" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Crescimento de Utilizadores" description="Novos registos por dia (últimos 30 dias)">
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={users?.growth || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                    <YAxis {...chartAxis} allowDecimals={false} />
                    <Tooltip {...chartTooltip} />
                    <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} name="Total acumulado" dot={false} />
                    <Line type="monotone" dataKey="new" stroke="#d4af37" strokeWidth={2} name="Novos no dia" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>
        )}

        {/* VISITORS */}
        {tab === 'visitors' && visitors && (
          <div className="space-y-6" data-testid="visitors-panel">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'week', label: 'Semana' },
                { id: 'month', label: 'Mês' },
                { id: 'all', label: 'Sempre' },
              ].map(p => (
                <Button
                  key={p.id}
                  size="sm"
                  variant={visitorsPeriod === p.id ? 'default' : 'outline'}
                  onClick={() => setVisitorsPeriod(p.id)}
                  className={visitorsPeriod === p.id ? 'bg-ruby' : 'border-border'}
                  data-testid={`period-${p.id}`}
                >
                  {p.label}
                </Button>
              ))}
              {visitors.tracking_started_at && (
                <span className="text-xs text-muted-foreground ml-2">
                  Tracking desde {new Date(visitors.tracking_started_at).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Hoje" value={visitors.visitors_today} icon={Globe} hint="Visitantes únicos com consentimento" />
              <StatCard title="Últimos 7 dias" value={visitors.visitors_week} icon={Globe} hint={consentFrom ? `Únicos com consentimento, desde ${consentFrom}` : 'Visitantes únicos com consentimento'} />
              <StatCard title="Últimos 30 dias" value={visitors.visitors_month} icon={Globe} hint={consentFrom ? `Únicos com consentimento, desde ${consentFrom}` : 'Visitantes únicos com consentimento'} />
              <StatCard title={consentFrom ? `Desde ${consentFrom} (com consentimento)` : 'Com consentimento'} value={visitors.visitors_all_time} icon={Users} color="gold" hint={visitors.consent_coverage != null ? `Cobertura de páginas vistas com consentimento: ${visitors.consent_coverage}% (não é % de visitantes)` : 'Cobertura de páginas vistas com consentimento: disponível após o 1.º dia completo'} />
              <StatCard title={visitorsPeriod === 'all' ? 'Recorrentes (desde o consentimento)' : 'Recorrentes'} value={visitors.returning_visitors} icon={Activity} hint="Com consentimento, em 2 ou mais dias diferentes" />
              <StatCard title="Page Views (Total)" value={(visitors.page_views_total || 0) + (visitors.anon_views_total || 0)} icon={Eye} color="gold" hint={`${visitors.page_views_total ?? 0} com consentimento · ${visitors.anon_views_total ?? 0} anónimas agregadas · ${visitors.bot_views_excluded ?? 0} de robôs excluídas`} />
            </div>
            <ChartCard title={`Tráfego diário — ${visitors.period === 'week' ? 'semana' : visitors.period === 'all' ? 'últimos 12 meses' : 'mês'}`}>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={visitors.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip {...chartTooltip} />
                  <Area type="monotone" dataKey="page_views" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} name="Page views" />
                  <Area type="monotone" dataKey="visitors" stroke="#9b111e" fill="#9b111e" fillOpacity={0.35} name="Visitantes" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && users && (
          <div className="space-y-6" data-testid="users-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Total Registados" value={users.total} icon={Users} color="blue-400" />
              <StatCard title="Novos Hoje" value={users.new_today} icon={Users} color="emerald-400" hint="Desde as 00h (Lisboa)" />
              <StatCard title="Novos (7 dias)" value={users.new_week} icon={Users} hint={`vs ${users.new_prev_week ?? 0} nos 7 dias anteriores`} />
              <StatCard title="Novos (30 dias)" value={users.new_month} icon={Users} hint={`vs ${users.new_prev_month ?? 0} nos 30 dias anteriores`} />
              <StatCard title="Ativos (30d)" value={users.active} icon={Activity} color="emerald-400" hint={`Visita com sessão, leitura ou ação · ${users.active_week ?? 0} em 7 dias`} />
              <StatCard title="Inativos" value={users.inactive} icon={Activity} color="gold" hint="Registados sem atividade em 30 dias" />
            </div>
            <ChartCard title="Crescimento de utilizadores (30 dias)">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={users.growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                  <YAxis {...chartAxis} allowDecimals={false} />
                  <Tooltip {...chartTooltip} />
                  <Area type="monotone" dataKey="total" stroke="#10b981" fill="#10b981" fillOpacity={0.25} name="Total acumulado" />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}

        {/* PREMIUM */}
        {tab === 'premium' && premium && (
          <div className="space-y-6" data-testid="premium-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Premium Ativos" value={premium.active} icon={Crown} color="gold" hint="Data de fim futura" />
              <StatCard title="Premium Expirados" value={premium.expired} icon={Clock} color="ruby" hint="Tiveram Premium e a data de fim já passou" />
              <StatCard title="Vendas (30 dias)" value={premium.sales_month} icon={TrendingUp} color="emerald-400" hint={`Compras pagas · ${premium.grants_month ?? 0} ativação(ões) manual(ais) à parte`} />
              <StatCard title="Assinantes Premium" value={`${premium.conversion_rate}%`} icon={Activity} color="blue-400" hint="Premium ativos ÷ utilizadores registados" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard title="Vendas por plano" description={`${premium.total_sales} venda(s) paga(s) · ${premium.revenue_total ?? 0} € brutos (valor real pago, com IVA)${premium.unmatched_sales ? ` · ${premium.unmatched_sales} sem plano correspondente` : ''}`}>
                {premium.total_sales > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={premium.plan_sales} dataKey="sales" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {premium.plan_sales.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground py-12 text-center">Sem vendas ainda.</p>
                )}
                <div className="mt-4 space-y-2">
                  {premium.plan_sales.map((p, i) => (
                    <div key={p.plan_id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                        <span className="text-foreground">{p.name}</span>
                      </div>
                      <span className="text-muted-foreground">{p.sales} vendas • {p.percent}% • €{p.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </ChartCard>

              <ChartCard title="Crescimento Premium (30 dias)" description="Acumulado de novas ativações (vendas + manuais)">
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={premium.growth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                    <YAxis {...chartAxis} allowDecimals={false} />
                    <Tooltip {...chartTooltip} />
                    <Area type="monotone" dataKey="total" stroke="#d4af37" fill="#d4af37" fillOpacity={0.25} name="Acumulado" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>
        )}

        {/* CONTENT */}
        {tab === 'content' && content && (
          <div className="space-y-6" data-testid="content-panel">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <TopList testid="top-stories" title="Contos Mais Vistos" items={content.top_stories} valueLabel="Vistas" valueKey="views" />
              <TopList testid="top-favorited-stories" title="Contos Mais Favoritados" items={content.top_favorited_stories} valueLabel="Favoritos" valueKey="favorites" />
              <TopList testid="top-completed-stories" title="Contos Mais Completados (≥90% lido)" items={content.top_completed_stories} valueLabel="Completos" valueKey="completions" />
              <TopList testid="top-series" title="Séries Mais Vistas" items={content.top_series} valueLabel="Vistas" valueKey="views" />
              <TopList testid="top-guides" title="Conhece-te Mais Vistos" items={content.top_guides} valueLabel="Vistas" valueKey="views" />
              <TopList testid="top-articles" title="+ Prazer Mais Vistos" items={content.top_articles} valueLabel="Vistas" valueKey="views" />
              <TopList testid="top-confessions" title="Confissões Mais Vistas" items={content.top_confessions} valueLabel="Vistas" valueKey="views" />
              <TopList testid="top-channels" title="Canais Mais Seguidos" items={content.top_channels} valueLabel="Seguidores" valueKey="followers" titleKey="name" />
              <TopList testid="top-channel-posts" title="Posts de Canais com mais Gostos" items={content.top_channel_posts} valueLabel="Likes" valueKey="likes_count" titleKey="preview" />
            </div>
          </div>
        )}

        {/* ENGAGEMENT */}
        {tab === 'engagement' && engagement && (
          <div className="space-y-6" data-testid="engagement-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <StatCard title="Duração média estimada" value={`${engagement.avg_reading_time} min`} icon={Clock} color="blue-400" hint="Estimativa editorial dos contos, não tempo real" />
              <StatCard title="Leituras iniciadas" value={engagement.total_sessions} icon={BookOpen} hint="Contos abertos por utilizadores com sessão (1 por pessoa e conto)" />
              <StatCard title="Leituras Completas" value={engagement.completed_sessions} icon={CheckCircle2} color="emerald-400" hint={`${engagement.completion_rate}% das leituras iniciadas chegaram a 90%`} />
              <StatCard title="Favoritos" value={engagement.total_favorites} icon={Heart} color="ruby" />
              <StatCard title="Partilhas" value={engagement.shares} icon={Share2} color="gold" />
              <StatCard title="Comentários" value={engagement.total_comments} icon={MessageSquare} color="purple-400" />
            </div>
          </div>
        )}

        {/* RETENTION */}
        {tab === 'retention' && (
          <div className="space-y-6" data-testid="retention-panel">
            {!retention ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-ruby" /></div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground" data-testid="retention-since">
                  Só contas registadas, por dia de atividade (sem páginas nem conteúdos). Fuso horário de Lisboa. {retSince ? `Histórico diário desde ${retSince}.` : 'Histórico diário ainda sem dados.'}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <StatCard testid="ret-active-7" title="Ativos (7 dias)" value={retention.active_week} icon={Activity} color="emerald-400" hint="Contas com visita, leitura ou ação" />
                  <StatCard testid="ret-active-30" title="Ativos (30 dias)" value={retention.active_month} icon={Activity} color="emerald-400" hint={`De ${retention.total_users} contas registadas`} />
                  <StatCard testid="ret-inactive-30" title="Inativos +30 dias" value={retention.inactive_month} icon={UserX} color="gold" hint="Sem atividade nos últimos 30 dias" />
                  <StatCard testid="ret-returning" title="Regressaram" value={retention.returning ?? 'Dados insuficientes'} icon={Repeat} color="blue-400" hint={retSince ? `Ativos em 2+ dias diferentes nos últimos 30 dias (desde ${retSince})` : 'Disponível após a recolha diária começar'} />
                  <StatCard testid="ret-d7" title="Retenção D7" value={rateValue(retention.d7)} icon={TrendingUp} color="purple-400" hint={rateHint(retention.d7, 7)} />
                  <StatCard testid="ret-d30" title="Retenção D30" value={rateValue(retention.d30)} icon={TrendingUp} color="ruby" hint={rateHint(retention.d30, 30)} />
                </div>
                <ChartCard testid="ret-chart" title="Contas ativas por dia" description={retSince ? `Contas registadas com atividade em cada dia (desde ${retSince})` : 'Sem dados diários ainda'}>
                  <div className="flex gap-2 mb-4">
                    {[7, 30, 90].map(d => (
                      <Button key={d} size="sm" variant={retentionDays === d ? 'default' : 'outline'} onClick={() => setRetentionDays(d)} className={retentionDays === d ? 'bg-ruby' : 'border-border'} data-testid={`ret-period-${d}`}>
                        {d} dias
                      </Button>
                    ))}
                  </div>
                  {retention.series.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-12 text-center" data-testid="ret-chart-empty">Dados insuficientes.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={retention.series}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                        <YAxis {...chartAxis} allowDecimals={false} />
                        <Tooltip {...chartTooltip} />
                        <Area type="monotone" dataKey="active" stroke="#10b981" fill="#10b981" fillOpacity={0.25} name="Contas ativas" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </ChartCard>
                <p className="text-xs text-muted-foreground">
                  Leituras iniciadas, concluídas e taxa de conclusão: ver o separador{' '}
                  <button className="text-ruby underline" onClick={() => setTab('engagement')} data-testid="ret-go-engagement">Engagement</button>.
                </p>
              </>
            )}
          </div>
        )}

        {/* GROWTH */}
        {tab === 'growth' && (
          <div className="space-y-6" data-testid="growth-panel">
            <div className="flex gap-2">
              {[7, 30, 90].map(d => (
                <Button key={d} size="sm" variant={growthDays === d ? 'default' : 'outline'} onClick={() => setGrowthDays(d)} className={growthDays === d ? 'bg-ruby' : 'border-border'} data-testid={`growth-period-${d}`}>
                  {d} dias
                </Button>
              ))}
            </div>
            {!growth ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-ruby" /></div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <StatCard testid="growth-new" title={`Novos registos (${growth.days}d)`} value={growth.new_period} icon={Users} color="emerald-400" hint={`vs ${growth.new_prev_period} nos ${growth.days} dias anteriores`} />
                  <StatCard testid="growth-free" title="Contas gratuitas" value={growth.free} icon={Users} color="blue-400" hint={`De ${growth.total_users} registadas`} />
                  <StatCard testid="growth-premium" title="Contas Premium" value={growth.premium} icon={Crown} color="gold" hint="Premium com acesso ativo" />
                  <StatCard testid="growth-inactive" title="Inativas +30 dias" value={growth.inactive_30} icon={UserX} color="ruby" hint="Sem visita, leitura ou ação em 30 dias" />
                  <StatCard testid="growth-conv-reg" title="Visitante → registo (aprox.)" value={growth.conv_registration ? `${growth.conv_registration.rate}%` : 'Dados insuficientes'} icon={TrendingUp} color="purple-400" hint={growth.conv_registration ? `${growth.conv_registration.registrations} registos ÷ ${growth.conv_registration.visitors} visitantes únicos com consentimento` : 'Precisa de todo o período após o início do consentimento'} />
                  <StatCard testid="growth-conv-premium" title="Visita Premium → compra (aprox.)" value={growth.conv_premium ? `${growth.conv_premium.rate}%` : 'Dados insuficientes'} icon={Crown} color="gold" hint={growth.conv_premium ? `${growth.conv_premium.purchases} compras ÷ ${growth.conv_premium.visits} visitas à página Premium` : 'Sem visitas à página Premium no período'} />
                </div>
                <ChartCard testid="growth-chart" title={`Novos registos e compras Premium (${growth.days} dias)`} description={`${growth.premium_sales_total} compra(s) paga(s) no período · ativações manuais excluídas`}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={growthSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="date" {...chartAxis} tickFormatter={(d) => d.slice(5)} />
                      <YAxis {...chartAxis} allowDecimals={false} />
                      <Tooltip {...chartTooltip} />
                      <Line type="monotone" dataKey="registos" stroke="#10b981" strokeWidth={2} dot={false} name="Novos registos" />
                      <Line type="monotone" dataKey="premium" stroke="#d4af37" strokeWidth={2} dot={false} name="Compras Premium" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ChartCard testid="growth-opportunities" title="Oportunidades" description="Regras simples sobre dados agregados reais">
                    <div className="space-y-3">
                      {growth.opportunities.map(o => (
                        <div key={o.key} className="flex gap-3 p-3 rounded-lg bg-white/5" data-testid={`opportunity-${o.key}`}>
                          <Lightbulb className={`w-4 h-4 mt-0.5 shrink-0 ${o.status === 'ok' ? 'text-gold' : 'text-muted-foreground'}`} />
                          <div className="min-w-0">
                            <p className="text-sm text-foreground">{o.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {o.status === 'insufficient' ? 'Dados insuficientes' : o.detail}{o.status === 'fine' ? ' — sem ação prioritária.' : ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ChartCard>
                  <TopList testid="growth-top-stories" title="Contos mais vistos (acumulado)" items={growth.top_stories} valueLabel="Vistas" valueKey="views" />
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'campaigns' && <CampaignsPanel />}

        {/* FUNNEL */}
        {tab === 'funnel' && funnel && (
          <div className="space-y-6" data-testid="funnel-panel">
            <ChartCard title="Funil de Conversão Premium (30 dias)" description="Visitantes únicos da página Premium → compras pagas (WooCommerce + Stripe)">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  layout="vertical"
                  data={[
                    { step: 'Visitas a /premium (todas)', value: funnel.premium_visits },
                    { step: 'Visitantes únicos (com consentimento)', value: funnel.unique_premium_visitors },
                    { step: 'Compras concluídas', value: funnel.purchases },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis type="number" {...chartAxis} allowDecimals={false} />
                  <YAxis type="category" dataKey="step" width={180} {...chartAxis} />
                  <Tooltip {...chartTooltip} />
                  <Bar dataKey="value" fill="#9b111e" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 rounded-lg bg-white/5">
                <p className="text-sm text-muted-foreground">Compras por cada 100 visitas à página Premium (indicador aproximado, não é conversão de visitantes únicos)</p>
                <p className="text-3xl font-bold text-gold mt-1">{funnel.conv_visit_to_buy}%</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {funnel.purchases} compras pagas a partir de {funnel.premium_visits} visitas à página Premium (com e sem consentimento).
                </p>
              </div>
            </ChartCard>
          </div>
        )}

        {/* SOURCES */}
        {tab === 'sources' && sources && (
          <div className="space-y-6" data-testid="sources-panel">
            <ChartCard title="Origem do Tráfego (30 dias)" description={`${sources.total_visits} visitas no total`}>
              {sources.sources.length === 0 ? (
                <p className="text-sm text-muted-foreground py-12 text-center">Sem visitas registadas ainda.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={sources.sources} dataKey="visits" nameKey="source" innerRadius={50} outerRadius={90} paddingAngle={2}>
                        {sources.sources.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip {...chartTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {sources.sources.map((s, i) => (
                      <div key={s.source} className="flex items-center justify-between p-2 bg-white/5 rounded-md">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                          <Badge variant="outline" className="capitalize border-border">{s.source}</Badge>
                        </div>
                        <span className="text-sm text-muted-foreground">{s.visits} visitas • {s.visitors} visitantes • {s.percent}%</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </ChartCard>
          </div>
        )}

        {/* INTEGRATIONS */}
        {tab === 'integrations' && (
          <div className="space-y-6 max-w-2xl" data-testid="integrations-panel">
            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <Settings className="w-5 h-5 text-ruby" />
                  Integrações Externas
                </CardTitle>
                <CardDescription>
                  Adiciona os teus IDs para enriquecer este dashboard com dados externos. Os scripts são carregados automaticamente no frontend (apenas para visitantes finais — não em /admin).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-foreground">Google Analytics 4 — Measurement ID</Label>
                  <Input
                    placeholder="G-XXXXXXXXXX"
                    value={settings.ga4_measurement_id || ''}
                    onChange={(e) => setSettings({ ...settings, ga4_measurement_id: e.target.value })}
                    className="bg-background border-border"
                    data-testid="input-ga4"
                  />
                  <p className="text-xs text-muted-foreground">
                    Obtém-no em analytics.google.com → Admin → Streams → Web. Formato: <code>G-XXXXXXXXXX</code>.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Microsoft Clarity — Project ID</Label>
                  <Input
                    placeholder="abcdefghij"
                    value={settings.clarity_project_id || ''}
                    onChange={(e) => setSettings({ ...settings, clarity_project_id: e.target.value })}
                    className="bg-background border-border"
                    data-testid="input-clarity"
                  />
                  <p className="text-xs text-muted-foreground">
                    Obtém-no em clarity.microsoft.com → Settings → Setup → Install tracking code.
                  </p>
                </div>
                <Button onClick={saveSettings} disabled={savingSettings} className="bg-ruby hover:bg-ruby-light" data-testid="save-integrations">
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Definições
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-surface border-border">
              <CardHeader>
                <CardTitle className="text-foreground text-base">Como funciona o tracking interno</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p>O dashboard usa tracking próprio (first-party) ativado em todas as páginas públicas:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Cookie httpOnly <code>intimate_visitor</code> identifica visitantes anónimos durante 1 ano</li>
                  <li>Sem cookies de terceiros — totalmente conforme com privacidade UE</li>
                  <li>Páginas <code>/admin</code> e <code>/embed</code> não contam para as métricas</li>
                  <li>Source classifier deteta Google, Instagram, Facebook, email (UTM), etc.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
